import { Splash } from "./Splash";
import { useContext, useEffect } from "react";
import { Toolbar } from "primereact/toolbar";
import { Wordmark } from "./components/wordmark/Wordmark";
import styles from "./LoggedOut.module.css";
import { motion } from "framer-motion";
import { ThemeContext } from "./themes/Theme";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ROUTES } from "./routes";
import { logEvent } from "./amplitude";
import { debounce } from "lodash";
import * as client from "./client";
import imgSrc from "./luminor-background.jpg";

type LoggedOutProps = {
  onSuccessfulAuth: (clientId: string, user: client.User | undefined) => void;
};

// Something about react-router remounts this component during initial page load, so only log one page visit event per 500ms.
const logVisit = debounce((params: { [k: string]: string }) => {
  logEvent("page_visit", { ...params, location: location.hash.substring(1) });
}, 500);

export const LoggedOut: React.FC<LoggedOutProps> = ({ onSuccessfulAuth }) => {
  const { setThemeOverride } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URL(window.location.href).searchParams;
    const params: { [query: string]: string } = {};
    searchParams.forEach((v, k) => {
      params[k] = v;
    });
    logVisit(params);
  }, [location.pathname]);

  useEffect(() => {
    setThemeOverride("dark");

    return () => {
      setThemeOverride(undefined);
    };
  }, [location.pathname, setThemeOverride]);

  return (
    <div className={styles.container}>
      <img src={imgSrc} className={styles.backgroundImg} />
      <Toolbar
        className={styles.toolbar}
        start={
          <Wordmark
            onClick={() => navigate(ROUTES.LANDING)}
            className={styles.wordmark}
          />
        }
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles.contentContainer}
        key={location.pathname}
      >
        <Routes>
          <Route
            path={ROUTES.LANDING}
            element={<Splash onSuccessfulAuth={onSuccessfulAuth} />}
          />
          <Route path="*" element={<Navigate to={ROUTES.LANDING} />} />
        </Routes>
      </motion.div>
    </div>
  );
};
